<template>
  <div class="ds-chart-panel">
    <div class="ds-chart-panel__head" v-if="title || $slots.actions">
      <span class="ds-chart-panel__title" v-if="title">{{ title }}</span>
      <span class="ds-chart-panel__subtitle" v-if="subtitle">{{ subtitle }}</span>
      <div class="ds-chart-panel__actions" v-if="$slots.actions">
        <slot name="actions"></slot>
      </div>
    </div>

    <div class="ds-chart-panel__body" :style="{ height: bodyHeight }">
      <div v-if="loading" class="ds-chart-panel__loading" v-loading="true" element-loading-text="加载中"></div>
      <div v-else-if="empty" class="ds-chart-panel__empty">
        <slot name="empty">
          <ds-empty-state type="no-data" title="暂无图表数据"></ds-empty-state>
        </slot>
      </div>
      <div v-else class="ds-chart-panel__content">
        <slot></slot>
      </div>
    </div>

    <!-- 无障碍：为图表提供等价文本摘要 -->
    <div v-if="summary" class="ds-chart-panel__summary">{{ summary }}</div>
  </div>
</template>

<script>
import DsEmptyState from './ds-empty-state.vue'

/**
 * DsChartPanel —— 图表容器
 *
 * 职责：统一标题、空态、加载态、等价文本摘要，并托管图表生命周期。
 *
 * Props:
 *  - title    {String}  标题
 *  - subtitle {String}  副标题（如口径说明）
 *  - loading  {Boolean} 加载态
 *  - empty    {Boolean} 空态
 *  - height   {String}  图表区高度
 *  - summary  {String}  等价文本摘要（无障碍）
 *
 * Slots:
 *  - default  图表 DOM（必须是带 ref 的元素）
 *  - actions  标题右侧动作
 *  - empty    自定义空态
 */
export default {
  name: 'DsChartPanel',
  components: { DsEmptyState },
  props: {
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    empty: {
      type: Boolean,
      default: false
    },
    height: {
      type: String,
      default: '320px'
    },
    summary: {
      type: String,
      default: ''
    }
  },
  computed: {
    bodyHeight () {
      return this.loading || this.empty ? '180px' : this.height
    }
  }
}
</script>

<style lang="scss" scoped>
.ds-chart-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  padding: var(--ds-spacing-md) var(--ds-spacing-lg) var(--ds-spacing-lg);

  &__head {
    display: flex;
    align-items: center;
    gap: var(--ds-spacing-sm);
    margin-bottom: var(--ds-spacing-sm);
  }

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }

  &__subtitle {
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
  }

  &__actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: var(--ds-spacing-sm);
  }

  &__body {
    position: relative;
    width: 100%;
  }

  &__loading {
    height: 100%;
  }

  &__empty {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__canvas {
    width: 100%;
    height: 100%;
  }

  &__content {
    width: 100%;
    height: 100%;
  }

  &__summary {
    margin-top: var(--ds-spacing-sm);
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
    line-height: 1.6;
  }
}
</style>
